<template>
  <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
    <!-- Header -->
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <div class="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
          <i class="pi pi-id-card text-sm"></i>
        </div>
        <h3 class="font-bold text-gray-800 text-sm">My Roles</h3>
      </div>
      <Button
        icon="pi pi-plus"
        size="small"
        text
        rounded
        severity="secondary"
        v-tooltip.left="'Request a Role'"
        @click="showRequestDialog = true"
      />
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-4">
      <ProgressSpinner style="width: 24px; height: 24px" />
    </div>

    <!-- Empty state -->
    <div v-else-if="!activeRoles.length && !pendingCount" class="text-center py-4">
      <p class="text-xs text-gray-400">No active roles</p>
      <button class="text-xs text-indigo-500 hover:underline mt-1" @click="showRequestDialog = true">
        Request a role
      </button>
    </div>

    <!-- Active roles -->
    <div v-else class="space-y-2">
      <div
        v-for="r in activeRoles.slice(0, 4)"
        :key="r.id"
        class="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50"
      >
        <i class="pi pi-check-circle text-primary text-xs"></i>
        <span class="text-xs font-medium text-gray-700 truncate flex-1">
          {{ r.permissionGroup?.name || '—' }}
        </span>
        <span v-if="r.scopes?.length" class="text-[10px] text-gray-400">
          {{ r.scopes.length }} scope{{ r.scopes.length > 1 ? 's' : '' }}
        </span>
      </div>

      <!-- Overflow -->
      <p v-if="activeRoles.length > 4" class="text-[10px] text-gray-400 text-center">
        +{{ activeRoles.length - 4 }} more
      </p>

      <!-- Pending badge -->
      <div v-if="pendingCount" class="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-100">
        <i class="pi pi-clock text-amber-500 text-xs"></i>
        <span class="text-xs text-amber-700">{{ pendingCount }} pending request{{ pendingCount > 1 ? 's' : '' }}</span>
      </div>
    </div>

    <!-- View all link -->
    <div v-if="roleRequests.length > 0" class="mt-3 pt-3 border-t border-gray-100">
      <router-link
        :to="{ name: 'admin-my-account' }"
        class="text-xs text-indigo-500 hover:text-indigo-700 flex items-center gap-1"
      >
        View all roles
        <i class="pi pi-arrow-right text-[10px]"></i>
      </router-link>
    </div>

    <!-- Request Dialog -->
    <Dialog
      v-model:visible="showRequestDialog"
      header="Request a Role"
      modal
      :closable="true"
      style="width: 420px"
    >
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-600">Select Role</label>
          <Select
            v-model="selectedGroupId"
            :options="roleOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Choose a role"
            :loading="loadingRoles"
            class="w-full"
          />
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end gap-2">
          <Button size="small" label="Cancel" text @click="showRequestDialog = false" />
          <Button size="small"
            label="Submit"
            icon="pi pi-send"
            :loading="requesting"
            @click="submitRequest"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import Tooltip from 'primevue/tooltip';
import { useMutation } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import { useMyRoleRequests } from '@/composables/mutations/useMyRoleRequests';
import { useRoleOptions } from '@/composables/useRoleOptions';

import { useRequestRoleMutation } from '@/types/vue-apollo-helpers';
import type { PermissionGrant } from '@/types/composition-functions';

const vTooltip = Tooltip;

const toast = useToast();

// Data
const { roleRequests, loading, refetch } = useMyRoleRequests();
const { roleOptions, loadingRoles } = useRoleOptions();

const activeRoles = computed(() =>
  roleRequests.value.filter((r: PermissionGrant) =>
    ['approved', 'active'].includes((r.status || '').toLowerCase()),
  ),
);

const pendingCount = computed(() =>
  roleRequests.value.filter((r: PermissionGrant) =>
    (r.status || '').toLowerCase() === 'pending',
  ).length,
);

// Request Dialog
const showRequestDialog = ref(false);
const selectedGroupId = ref<string | null>(null);
const requesting = ref(false);
const { mutate: requestRoleMutate } = useRequestRoleMutation();

const submitRequest = async() => {
  if (!selectedGroupId.value) {
    toast.add({ severity: 'warn', summary: 'Required', detail: 'Select a role', life: 3000 });
    return;
  }

  requesting.value = true;
  try {
    await requestRoleMutate({
      input: {
        school_permission_group_id: selectedGroupId.value,
      },
    });
    toast.add({ severity: 'success', summary: 'Submitted', detail: 'Role request submitted', life: 3000 });
    showRequestDialog.value = false;
    selectedGroupId.value = null;
    refetch();
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Failed';
    toast.add({ severity: 'error', summary: 'Error', detail: message, life: 4000 });
  } finally {
    requesting.value = false;
  }
};
</script>
